import { useTranslations } from 'use-intl';
import Table from '../../modules/Table';
import InfiniteScroll from '../../modules/InfiniteScroll';
import Icon, { Icons } from '../../elements/Icon';
import { useMemo, useState } from 'react';
import CollectionRow from './CollectionRow';
import { Collection } from './types';
import { CollectionsHeader } from './Header';
import { getBestBuyPrice, getTvl } from '@/application/read/vaults/utils';

export interface CollectionsProps {
  collections?: Collection[];
  isLoading?: boolean;
}

const CollectionsTableSpinner = () => {
  return (
    <td colSpan={6} className="p-4 text-center">
      <Icon
        name={Icons.SPINNER_GAP}
        className="animate animate-spin inline-flex text-gray-500"
        size={24}
      />
    </td>
  );
};

const Collections = ({ collections = [], isLoading }: CollectionsProps) => {
  const t = useTranslations('Collections');
  const [visible, setVisible] = useState(10);

  const visibleCollections = useMemo(() => {
    return collections
      ?.slice()
      .sort((a, b) => {
        return getTvl(a.vaults) > getTvl(b.vaults) ? -1 : 1;
      })
      .slice(0, visible);
  }, [collections, visible]);

  return (
    <section>
      <CollectionsHeader
        title={t('title')}
        subtitle={t('text')}
        collections={collections}
      />
      <section>
        <Table
          header={
            <header className="flex justify-between items-center py-4 px-3 border-b border-gray-300">
              <h3 className="font-bold">{t('table.header')}</h3>
            </header>
          }
          headings={[
            {
              label: t('table.name'),
              align: 'left',
            },
            {
              label: t('table.listings'),
            },
            {
              label: t('table.price'),
            },
            {
              label: t('table.sellPrice'),
            },
            {
              label: t('table.volume'),
            },
          ]}
        >
          <InfiniteScroll
            onLoadMore={() => setVisible((x) => x + 10)}
            scrollable={collections?.length > visible}
            loaderElement="tr"
            loaderClassName="table-row"
            loaderChildren={<CollectionsTableSpinner />}
          >
            {visibleCollections.map((collection, i) => (
              <CollectionRow
                key={collection.collectionAddress}
                {...collection}
              />
            ))}
            {isLoading && (
              <tr>
                <CollectionsTableSpinner />
              </tr>
            )}
          </InfiniteScroll>
        </Table>
      </section>
    </section>
  );
};

export default Collections;
